import React, { useMemo, useRef, useState, useEffect } from "react";
import axios from "axios";
import { getProjectPid } from "../utils/sessionProject";
import { formatTicketPid } from "../utils/formatPid";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Activity,
  PieChart,
  TrendingUp,
  Globe,
  BarChart3,
  TicketCheck,
  ChevronRight,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart as RePieChart,
  Pie,
  Cell,
} from "recharts";
const TICKETS = [
  { id: "TKT-001", subject: "Login issue",         status: "open",    priority: "high",   created: "Jun 10, 2026" },
  { id: "TKT-002", subject: "Dashboard not loading",status: "pending", priority: "medium", created: "Jun 11, 2026" },
  { id: "TKT-003", subject: "Export CSV broken",    status: "closed",  priority: "low",    created: "Jun 12, 2026" },
  { id: "TKT-004", subject: "Password reset email", status: "open",    priority: "high",   created: "Jun 13, 2026" },
  { id: "TKT-005", subject: "Billing inquiry",      status: "pending", priority: "medium", created: "Jun 14, 2026" },
];

const STATUS_STYLE = {
  open: { bg: "#eff6ff", color: "#2563eb", dot: "#3b82f6" },
  processing: { bg: "#fff7ed", color: "#ea580c", dot: "#f97316" },
  closed: { bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" },
};
const DEFAULT_STATUS_STYLE = { bg: "#f8fafc", color: "#475569", dot: "#94a3b8" };
const getStatusStyle = (status) => STATUS_STYLE[status] || DEFAULT_STATUS_STYLE;
const toTitleCase = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

const formatDateLabel = (label) => {
  if (typeof label !== "string") return label;
  const m = label.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return label;
  const [, year, month, day] = m;
  return `${month}/${day}/${year}`;
};

const formatMillions = (value) => {
  const num = Number(value) || 0;
  return `${(num / 1_000_000).toFixed(4).replace(/0+$/, "").replace(/\.$/, ".0")} million`;
};

const PERIODS = [
  { key: "week", label: "This Week", apiPeriod: "this_week" },
  { key: "month", label: "This Month", apiPeriod: "this_month" },
  { key: "year", label: "This Year", apiPeriod: "this_year" },
];

const PERIOD_DATA = {
  week: {
    stats: [
      { title: "Total Tokens", value: "8,450", sub: "This week", icon: <Activity size={22} />, progress: "84%" },
      { title: "Requests", value: "1,248", sub: "Successful calls", icon: <BarChart3 size={22} /> },
      { title: "Unique Users", value: "342", sub: "Active visitors", icon: <Users size={22} /> },
      { title: "Avg Tokens / User", value: "24.7", sub: "Per active user", icon: <PieChart size={22} /> },
    ],
    barData: [
      { name: "Mon", value: 1200 },
      { name: "Tue", value: 2100 },
      { name: "Wed", value: 1800 },
      { name: "Thu", value: 2800 },
      { name: "Fri", value: 2400 },
      { name: "Sat", value: 3200 },
      { name: "Sun", value: 2600 },
    ],
    lineData: [
      { name: "Mon", value: 140 },
      { name: "Tue", value: 220 },
      { name: "Wed", value: 180 },
      { name: "Thu", value: 310 },
      { name: "Fri", value: 260 },
      { name: "Sat", value: 390 },
      { name: "Sun", value: 330 },
    ],
    pieData: [{ name: "Growth", value: 87 }, { name: "Remaining", value: 13 }],
    pieLabel: "87% Weekly Growth",
    countries: [
      { name: "India", users: 150, tokens: 4200, avg: 28 },
      { name: "USA", users: 72, tokens: 2100, avg: 29 },
      { name: "UK", users: 45, tokens: 1180, avg: 26 },
      { name: "Canada", users: 30, tokens: 780, avg: 26 },
      { name: "Germany", users: 18, tokens: 520, avg: 29 },
    ],
  },
  month: {
    stats: [
      { title: "Total Tokens", value: "34,200", sub: "This month", icon: <Activity size={22} />, progress: "68%" },
      { title: "Requests", value: "5,910", sub: "Successful calls", icon: <BarChart3 size={22} /> },
      { title: "Unique Users", value: "1,180", sub: "Active visitors", icon: <Users size={22} /> },
      { title: "Avg Tokens / User", value: "29.0", sub: "Per active user", icon: <PieChart size={22} /> },
    ],
    barData: [
      { name: "Week 1", value: 7200 },
      { name: "Week 2", value: 9100 },
      { name: "Week 3", value: 8400 },
      { name: "Week 4", value: 9500 },
    ],
    lineData: [
      { name: "Week 1", value: 1100 },
      { name: "Week 2", value: 1640 },
      { name: "Week 3", value: 1480 },
      { name: "Week 4", value: 1690 },
    ],
    pieData: [{ name: "Growth", value: 72 }, { name: "Remaining", value: 28 }],
    pieLabel: "72% Monthly Growth",
    countries: [
      { name: "India", users: 520, tokens: 15800, avg: 30 },
      { name: "USA", users: 310, tokens: 8900, avg: 29 },
      { name: "UK", users: 198, tokens: 5100, avg: 26 },
      { name: "Canada", users: 134, tokens: 3600, avg: 27 },
      { name: "Germany", users: 88, tokens: 2300, avg: 26 },
    ],
  },
  year: {
    stats: [
      { title: "Total Tokens", value: "4,12,000", sub: "This year", icon: <Activity size={22} />, progress: "91%" },
      { title: "Requests", value: "72,480", sub: "Successful calls", icon: <BarChart3 size={22} /> },
      { title: "Unique Users", value: "14,200", sub: "Active visitors", icon: <Users size={22} /> },
      { title: "Avg Tokens / User", value: "29.0", sub: "Per active user", icon: <PieChart size={22} /> },
    ],
    barData: [
      { name: "Jan", value: 28000 },
      { name: "Feb", value: 31000 },
      { name: "Mar", value: 27000 },
      { name: "Apr", value: 34000 },
      { name: "May", value: 38000 },
      { name: "Jun", value: 42000 },
      { name: "Jul", value: 39000 },
      { name: "Aug", value: 44000 },
      { name: "Sep", value: 36000 },
      { name: "Oct", value: 41000 },
      { name: "Nov", value: 35000 },
      { name: "Dec", value: 17000 },
    ],
    lineData: [
      { name: "Jan", value: 4200 },
      { name: "Feb", value: 5100 },
      { name: "Mar", value: 4800 },
      { name: "Apr", value: 6200 },
      { name: "May", value: 7100 },
      { name: "Jun", value: 8000 },
      { name: "Jul", value: 7400 },
      { name: "Aug", value: 8500 },
      { name: "Sep", value: 6900 },
      { name: "Oct", value: 7800 },
      { name: "Nov", value: 6500 },
      { name: "Dec", value: 3200 },
    ],
    pieData: [{ name: "Growth", value: 91 }, { name: "Remaining", value: 9 }],
    pieLabel: "91% Yearly Growth",
    countries: [
      { name: "India", users: 6200, tokens: 182000, avg: 29 },
      { name: "USA", users: 3800, tokens: 109000, avg: 29 },
      { name: "UK", users: 2400, tokens: 63000, avg: 26 },
      { name: "Canada", users: 1600, tokens: 43000, avg: 27 },
      { name: "Germany", users: 1100, tokens: 28000, avg: 25 },
    ],
  },
};

const PeriodDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = PERIODS.find((p) => p.key === value);

  return (
    <div className="chart-period-wrap" ref={ref}>
      <button className="chart-period-btn" onClick={() => setOpen((o) => !o)}>
        {current.label}
        <ChevronDown size={13} className={`chart-period-chevron ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="chart-period-dropdown">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`chart-period-option ${p.key === value ? "active" : ""}`}
              onClick={() => { onChange(p.key); setOpen(false); }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();

  const [barPeriod, setBarPeriod] = useState("week");
  const [linePeriod, setLinePeriod] = useState("week");
  const [piePeriod, setPiePeriod] = useState("week");
  const [countryPeriod, setCountryPeriod] = useState("week");

  const [apiStats, setApiStats] = useState(null);
  const [barData, setBarData] = useState(PERIOD_DATA.week.barData);
  const [barLoading, setBarLoading] = useState(false);
  const [lineData, setLineData] = useState(PERIOD_DATA.week.lineData);
  const [lineLoading, setLineLoading] = useState(false);

  const projectPid = getProjectPid();
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!projectPid) return;
    axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/ai/dashboard-stats/${projectPid}`, {
      headers: authHeader,
    }).then(res => {
      const r = res.data?.response;
      if (r) setApiStats(r);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!projectPid) return;
    const period = PERIODS.find(p => p.key === barPeriod)?.apiPeriod || 'this_week';
    setBarLoading(true);
    axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/ai/graph/tokens/${projectPid}?period=${period}`, {
      headers: authHeader,
    }).then(res => {
      const data = res.data?.response?.data || [];
      setBarData(data.map(d => ({ name: d.label, value: d.tokens })));
    }).catch(() => {
      setBarData(PERIOD_DATA[barPeriod]?.barData || []);
    }).finally(() => setBarLoading(false));
  }, [barPeriod]);

  useEffect(() => {
    if (!projectPid) return;
    const period = PERIODS.find(p => p.key === linePeriod)?.apiPeriod || 'this_week';
    setLineLoading(true);
    axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/ai/graph/requests/${projectPid}?period=${period}`, {
      headers: authHeader,
    }).then(res => {
      const data = res.data?.response?.data || [];
      setLineData(data.map(d => ({ name: d.label, value: d.requests })));
    }).catch(() => {
      setLineData(PERIOD_DATA[linePeriod]?.lineData || []);
    }).finally(() => setLineLoading(false));
  }, [linePeriod]);

  const stats = [
    {
      title: "Total Tokens",
      value: apiStats ? apiStats.totalTokens.toLocaleString() : PERIOD_DATA.week.stats[0].value,
      sub: "This week",
      icon: <Activity size={22} />,
      progress: "84%",
    },
    {
      title: "Requests",
      value: apiStats ? apiStats.totalRequests.toLocaleString() : PERIOD_DATA.week.stats[1].value,
      sub: "Successful calls",
      icon: <BarChart3 size={22} />,
    },
    {
      title: "Unique Users",
      value: apiStats ? apiStats.uniqueUsers.toLocaleString() : PERIOD_DATA.week.stats[2].value,
      sub: "Active visitors",
      icon: <Users size={22} />,
    },
    {
      title: "Avg Tokens / User",
      value: apiStats ? apiStats.avgTokensPerUser.toLocaleString() : PERIOD_DATA.week.stats[3].value,
      sub: "Per active user",
      icon: <PieChart size={22} />,
    },
  ];

  const [ticketCounts, setTicketCounts] = useState({ total: 0 });
  const [recentTickets, setRecentTickets] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const projectPid = getProjectPid();
    if (!token || !projectPid) return;
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

    fetch(`${process.env.REACT_APP_BASE_URL}/api/v1/support-tickets/counts`, {
      method: "POST",
      headers,
      body: JSON.stringify({ projectPid }),
    }).then(r => r.json()).then(data => {
      if (data.status) {
        setTicketCounts(data.response || { total: 0 });
      }
    }).catch(() => {});

    fetch(`${process.env.REACT_APP_BASE_URL}/api/v1/support-tickets/list`, {
      method: "POST",
      headers,
      body: JSON.stringify({ projectPid, page: 1, limit: 5 }),
    }).then(r => r.json()).then(data => {
      if (data.status) {
        const items = data.response?.items || [];
        setRecentTickets(items.map(t => ({
          id: t.ticketPid,
          subject: t.subject,
          status: t.status,
          date: t.createdAt ? new Date(t.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "",
        })));
      }
    }).catch(() => {});
  }, []);

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* ── Stats ── */}
        <div className="stats-grid">
          {stats.map((item, index) => (
            <div className="stat-card" key={index}>
              <div className="stat-top">
                <div className="stat-icon">{item.icon}</div>
                <div className="stat-content">
                  <h3>{item.value}</h3>
                  <p>{item.title}</p>
                  <span>{item.sub}</span>
                </div>
              </div>
              {item.progress && (
                <div className="progress-wrap">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: item.progress }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className="chart-grid">
          <div className="chart-card">
            <div className="card-head">
              <h3>Token Usage</h3>
              <PeriodDropdown value={barPeriod} onChange={setBarPeriod} />
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={barLoading ? [] : barData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickFormatter={formatDateLabel} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={formatMillions} />
                  <Tooltip formatter={(value) => formatMillions(value)} labelFormatter={formatDateLabel} />
                  <Bar dataKey="value" fill="var(--primaryColor)" radius={[6, 6, 0, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-card">
            <div className="card-head">
              <h3>Requests Trend</h3>
              <PeriodDropdown value={linePeriod} onChange={setLinePeriod} />
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={lineLoading ? [] : lineData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickFormatter={formatDateLabel} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={formatDateLabel} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="var(--primaryColor)"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>


        {/* ── Support Tickets ── */}
        <div className="db-tickets-row">
          <div className="chart-card db-tickets-overview">
            <div className="card-head mb-0">
              <div className="d-flex align-items-center gap-2">
                <TicketCheck size={17} color="var(--primaryColor)" />
                <h3>Support Tickets</h3>
              </div>
            </div>

            <div className="db-ts-total-row">
              <span className="db-ts-total-num">{ticketCounts.total}</span>
              <span className="db-ts-total-label">Total Tickets</span>
            </div>

            <div className="db-ts-stats">
              {Object.entries(ticketCounts)
                .filter(([key]) => key !== "total")
                .map(([status, count]) => ({
                  label: toTitleCase(status),
                  count,
                  dot: getStatusStyle(status).dot,
                  pct: ticketCounts.total ? Math.round((count / ticketCounts.total) * 100) : 0,
                }))
                .map((s) => (
                <div className="db-ts-stat-row" key={s.label}>
                  <span className="db-ts-dot" style={{ background: s.dot }} />
                  <span className="db-ts-stat-label">{s.label}</span>
                  <div className="db-ts-bar-wrap">
                    <div className="db-ts-bar-fill" style={{ width: `${s.pct}%`, background: s.dot }} />
                  </div>
                  <span className="db-ts-count">{s.count}</span>
                </div>
              ))}
            </div>

            <button className="db-ts-view-btn" onClick={() => navigate("/support-ticket")}>
              View All Tickets <ArrowRight size={14} />
            </button>
          </div>

          <div className="chart-card db-tickets-recent">
            <div className="card-head mb-1">
              <h3>Recent Tickets</h3>
              <button className="db-ts-link-btn" onClick={() => navigate("/support-ticket")}>
                See all <ChevronRight size={13} />
              </button>
            </div>

            <div className="db-recent-list">
              {recentTickets.map((ticket) => {
                const ss = getStatusStyle(ticket.status);
                return (
                  <div
                    key={ticket.id}
                    className="db-recent-row"
                    onClick={() => navigate(`/support-ticket-chat/${ticket.id}`)}
                  >
                    <div className="db-recent-info">
                      <p className="db-recent-subject">{ticket.subject}</p>
                      <p className="db-recent-meta">
                        <span className="db-recent-id">{formatTicketPid(ticket.id)}</span>
                        <span>·</span>
                        <span>{ticket.date}</span>
                      </p>
                    </div>
                    <span className="db-recent-badge" style={{ background: ss.bg, color: ss.color }}>
                      {toTitleCase(ticket.status)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </main>
  );
};

export default Dashboard;
